
import { Users, Shield, FileCheck, Presentation, BarChart3, BadgeDollarSign } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const VCISO = () => {
  return (
    <LandingPageTemplate
      title="Virtual CISO (vCISO) Services"
      description="Gain executive security leadership without the full-time cost. Our vCISO services provide expert guidance from experienced security leaders."
      keywords={[
        "virtual CISO", 
        "vCISO", 
        "security leadership", 
        "fractional CISO", 
        "cybersecurity executive", 
        "security strategy"
      ]}
      canonicalUrl="/cybersecurity/vciso/"
      schemaData={{
        name: "Virtual CISO (vCISO) Services",
        description: "Executive security leadership and strategy development from experienced security professionals at a fraction of the cost.",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Expert Security Leadership with"
        highlighted="Virtual CISO Services"
        description="Access experienced security leadership to guide your cybersecurity program without the overhead of a full-time executive hire."
        ctaText="Schedule a Consultation"
        secondaryButtonText="View Services"
        secondaryButtonLink="#services"
        modalTitle="Virtual CISO Consultation"
        modalDescription="Tell us about your security leadership needs and challenges, and we'll help you determine how our vCISO services can support your organization."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Virtual CISO",
          service_interest: "Security Leadership"
        }}
      />

      <FeatureSection 
        id="services"
        title="Comprehensive vCISO Services"
        subtitle="SECURITY LEADERSHIP"
        description="Our virtual CISO services provide the strategic guidance and tactical expertise your organization needs."
        background="light"
        features={[
          {
            title: "Security Strategy Development",
            description: "Creation and management of your cybersecurity strategy, roadmap, and program governance.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Policy & Compliance Management",
            description: "Development and maintenance of security policies, standards, and compliance requirements.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk Management",
            description: "Identification, assessment, and mitigation of cybersecurity risks aligned with business objectives.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Board & Executive Reporting",
            description: "Clear communication of security posture, priorities, and metrics to leadership teams.",
            icon: <Presentation className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Security Team Development",
            description: "Guidance on security team structure, skills development, and resource planning.",
            icon: <Users className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Budget Planning & Optimization",
            description: "Strategic allocation of security resources and budget to maximize risk reduction.",
            icon: <BadgeDollarSign className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection 
        title="Our vCISO Engagement Model"
        subtitle="METHODOLOGY"
        description="A flexible approach tailored to your organization's specific needs and maturity level."
        steps={[
          {
            title: "Initial Assessment",
            description: "Evaluate current security posture, risks, compliance requirements, and business objectives."
          },
          {
            title: "Program Development",
            description: "Create or enhance your security strategy, governance model, and program documentation."
          },
          {
            title: "Ongoing Management",
            description: "Regular oversight of security operations, project management, and continuous improvement."
          },
          {
            title: "Strategic Guidance",
            description: "Participation in executive meetings, board presentations, and strategic planning sessions."
          }
        ]}
        layout="horizontal"
      />

      <BenefitSection 
        title="Benefits of Virtual CISO Services"
        subtitle="BUSINESS VALUE"
        background="gradient"
        benefits={[
          {
            title: "Cost Efficiency",
            description: "Gain C-level security expertise at a fraction of the cost of a full-time executive."
          },
          {
            title: "Immediate Expertise",
            description: "Access experienced security leaders without lengthy recruitment processes."
          },
          {
            title: "Strategic Focus",
            description: "Develop a security program aligned with business objectives and risk tolerance."
          },
          {
            title: "Compliance Confidence",
            description: "Navigate complex regulatory requirements with expert guidance and documentation."
          },
          {
            title: "Board Confidence",
            description: "Provide assurance to board members and investors with professional security leadership."
          },
          {
            title: "Flexible Engagement",
            description: "Scale security leadership services based on your changing business needs."
          }
        ]}
      />

      <CtaSection 
        title="Ready to Strengthen Your Security Leadership?"
        description="Schedule a consultation to discuss how our vCISO services can support your cybersecurity program."
        ctaText="Get Started With vCISO Services"
        modalTitle="Virtual CISO Consultation"
        modalDescription="Let's discuss your security leadership needs and how our vCISO services can help your organization."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Virtual CISO",
          service_interest: "Security Leadership"
        }}
      />
    </LandingPageTemplate>
  );
};

export default VCISO;
